import React, { useCallback, useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import Mousetrap from 'mousetrap';
// We have deprecated unstated. Please use react-sweet-state instead
import { Subscribe } from 'unstated';
import { css } from '@compiled/react';

import CrossIcon from '@atlaskit/icon/core/close';
import Button from '@atlaskit/button/new';
import CommentIcon from '@atlaskit/icon/core/comment';
import { Box, Text, xcss, Inline } from '@atlaskit/primitives';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import {
	calculateElementOffset,
	getSidebarId,
	scrollToElementInEditor,
	scrollTargetIntoViewIfNeeded,
} from '@confluence/comments-util';
import {
	ReactionsProvider,
	useCommentsContentActions,
	useCommentsContentState,
} from '@confluence/comment-context';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useShowCommentsPanel } from '@confluence/comments-panel';
import { CommentIconWithUnread } from '@confluence/object-sidebar-components';
import {
	useCommentsPanelActions,
	ViewValues,
	useCommentsPanelScroll,
} from '@confluence/comments-panel-utils';
import { DialogsStateContainer } from '@confluence/dialogs';
import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import { fg } from '@confluence/feature-gating';
import {
	SidebarContainer,
	CloseButtonContainer,
	CloseButton,
	TopContainer,
} from '@confluence/inline-comments-common/entry-points/styled';
import type { Comment } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes';
import { MIN_PANEL_WIDTH, useIsObjectSidebarShown } from '@confluence/object-sidebar-api';
import {
	INLINE_COMMENTS_SHORTCUT,
	UNIVERSAL_TOGGLE_INLINE_COMMENTS_SHORTCUT,
	ShortcutVisualizer,
} from '@confluence/shortcuts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';

import { i18n, STICKY_COMMENT_VERTICAL_OFFSET_VIEW } from './inlineCommentConstants';
import { CommentNavigation } from './CommentNavigation';

export type CommentNavigationOptions = {
	commentData: Comment;
	disableCommentCount: boolean;
	onNavigationClick: (nextMarkerRef: string) => void;
};

export const handleIntersectionOld = (
	entry: IntersectionObserverEntry,
	setIsSticky: (_isSticky: boolean) => void,
	setSidebarOffset: (_offset: number) => void,
	sidebarOffset: number,
) => {
	if (entry.intersectionRatio === 0) {
		setIsSticky(false);
		setSidebarOffset(sidebarOffset);
	} else {
		setIsSticky(true);
		setSidebarOffset(STICKY_COMMENT_VERTICAL_OFFSET_VIEW);
	}
};

type CommentSidebarProps = {
	pageId: string;
	onClose: ((hasContentChanged?: boolean) => void) | undefined;
	annotationElement?: HTMLElement | null;
	navigationOptions?: CommentNavigationOptions;
	isEditor?: boolean;
	isViewCommentMode?: boolean;
	children: ReactNode;
	sidebarOffset: number;
	commentId?: string;

	/**
	 * Should scroll comment sidebar into view after mounting?
	 * When creating comment: scroll the entire sidebar into view;
	 * When viewing comment: scroll the first comment into view only to cater for long comment threads.
	 */
	scrollIntoView?: boolean;
	isOpeningMediaCommentFromToolbar?: boolean;
	sidebarEl: React.MutableRefObject<HTMLElement | null>;
	isSticky: boolean;
	annotationId?: string;
	inlineCommentRef?: (node: any) => void;
	nudgeRef?: React.MutableRefObject<HTMLElement | null>;
};

const redesignedViewAllStyle = xcss({
	position: 'absolute',
	right: 'space.500', // This gives us 4px between the X and the comment icon
	top: 'space.150', // This lines us up exactly with X
});

const viewAllContainerStyles = xcss({
	position: 'absolute',
	top: 'space.150',
	left: 'space.075',
	alignItems: 'center',
	justifyContent: 'center',
});

const commentHeaderStyle = xcss({
	flexDirection: 'column',
	display: 'flex',
	marginTop: 'space.400',
});

const commentSidebarHiddenStyle = css({
	display: 'none',
});

const commentEditorStyle = xcss({
	marginTop: 'space.0',
});

// We need to still technically show the element as it appears so it's screen announced
const commentSidebarAnnouncementContainer = css({
	height: 0,
	width: 0,
	color: 'transparent',
});

export const CommentSidebar: FC<CommentSidebarProps> = ({
	pageId,
	onClose,
	annotationElement,
	navigationOptions,
	isEditor,
	isViewCommentMode,
	children,
	sidebarOffset,
	commentId,
	scrollIntoView,
	isOpeningMediaCommentFromToolbar,
	sidebarEl,
	annotationId,
	inlineCommentRef = () => {},
	nudgeRef,
	isSticky,
}) => {
	const [isRefSet, setRef] = useState<boolean>(false);
	const [oldIsSticky, setOldIsSticky] = useState<boolean>(false);
	const [newSidebarOffset, setSidebarOffset] = useState<number>(sidebarOffset);
	const [showA11yAnnouncement, setShowA11yAnnouncement] = useState(false);

	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();
	const [{ isViewMode: isLiveViewMode }] = useLivePageMode();
	const { showCommentsPanel } = useShowCommentsPanel();

	const { setCurrentlySelectedThreadKey } = useCommentsPanelActions();
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();

	useEffect(() => {
		if (fg('confluence_frontend_remove_duplicate_sidebar_logic')) {
			return;
		}

		let offset = 0;

		if (annotationElement && !isEditor) {
			let scrollParent: HTMLElement | null;

			if (isEditor) {
				scrollParent = document.querySelector('.fabric-editor-popup-scroll-parent');
				offset = calculateElementOffset(annotationElement, scrollParent);
			} else {
				/*
				There's two cases here: a view comment and a create comment.
				We need the correct scrollParent for each case.
				Since it's not the editor in this logical branch we should use #content as the scrollParent.
				*/
				scrollParent = document.querySelector('div#content[data-inline-comments-target="true"]');
				if (scrollParent) {
					let annotationOffset = 0;
					if (isViewCommentMode) {
						// If the sidebar is rendering a view comment, we can use the annotationElement directly.
						annotationOffset = calculateElementOffset(annotationElement);
					} else {
						/*
					If the sidebar is rendering a create comment the annotationElement is actually the wrapperDOM
					we need to find the actual highlight instead. We can find this by looking for the mark because
					there will always be a mark before the create comment button is clicked.
					*/
						const actualHiglightElement: HTMLElement | null = scrollParent.querySelector(
							`[data-annotation-draft-mark="true"]`,
						);
						annotationOffset = calculateElementOffset(actualHiglightElement, undefined, true);
					}
					offset = annotationOffset + scrollParent.offsetTop;
				}
			}
			setSidebarOffset(offset);
		}
	}, [annotationElement, isEditor, isViewCommentMode]);

	useEffect(() => {
		// Scroll comments sidebar into view in editor
		// When creating comment: always scroll into view if needed (see CreateComment)
		// When viewing comment: only scroll into view if there's no focused comment or unread comment (see InlineComment)
		if (isRefSet && sidebarEl.current && isEditor) {
			if (fg('confluence_frontend_media_scroll_fix')) {
				if (isOpeningMediaCommentFromToolbar && annotationElement) {
					scrollToElementInEditor({
						targetElement: annotationElement,
						viewingRoomOffset: -150,
					});
				}
				// NOTE: This code path cannot currently be hit as the feature gate above is at 100%
				// This is likely unexpected (and the checks nested under the above condition are likely expected to be
				// included in the above condition)
			} else {
				if (scrollIntoView) {
					const scrollParent = document.querySelector('.fabric-editor-popup-scroll-parent');

					const target = isViewCommentMode
						? // If viewing the comments, scroll the first comment into view only to cater for long comment threads
							sidebarEl.current.querySelector(`[data-comment-id="${commentId}"]`)
						: sidebarEl.current;

					if (scrollParent && target) {
						scrollTargetIntoViewIfNeeded(target, scrollParent);
					}
				}
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isRefSet,
		scrollIntoView,
		annotationElement,
		isOpeningMediaCommentFromToolbar,
		commentId,
		isViewCommentMode,
		isEditor,
	]);

	useEffect(() => {
		let mouseTrap: any;
		if (isRefSet && sidebarEl.current) {
			mouseTrap = new Mousetrap(sidebarEl.current);
			// prevent saving via mod+enter from also publishing the page in edit mode
			mouseTrap.bind(INLINE_COMMENTS_SHORTCUT, (event: Event) => {
				event.stopPropagation();
			});

			if (!showA11yAnnouncement) {
				// We need to show this on a timer so the `aria-live` listener will respond with our message
				setTimeout(() => {
					setShowA11yAnnouncement(true);
				}, 100);
			}

			// When the sidebar is shown in the renderer, focus this element
			if (!isEditor && fg('confluence_frontend_comment_a11y_fixes')) {
				const firstCommentContainer = document.querySelector('[data-comment-id]');
				if (firstCommentContainer) {
					(firstCommentContainer as HTMLElement).focus();
				}
			}
		}

		return () => {
			if (mouseTrap) {
				mouseTrap.reset();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isRefSet]);

	const handlePropagation = (event: any) => {
		/**
		 * prevents an event bubbling up to removing focus
		 * from the highlight displaying the sidebar
		 */
		event.stopPropagation();
	};

	const handleOpeningCommentsPanel = useCallback(() => {
		showCommentsPanel({ openWithView: ViewValues.OPEN });
		if (annotationId) {
			setCurrentlySelectedThreadKey(annotationId);
			scrollCommentsPanelToThread({ annotationId });
		}
	}, [showCommentsPanel, setCurrentlySelectedThreadKey, scrollCommentsPanelToThread, annotationId]);

	const handleOnClose = useCallback(
		(dialogs: DialogsStateContainer, isOpeningCommentsPanel: boolean = false) => {
			if (hasContentChanged) {
				dialogs.showModal(CommentWarningDialog, {
					onConfirm: () => {
						resetContentChanged();
						isOpeningCommentsPanel ? handleOpeningCommentsPanel() : onClose && onClose(false);
					},
				});
			} else {
				isOpeningCommentsPanel
					? handleOpeningCommentsPanel()
					: onClose && onClose(hasContentChanged);
			}
		},
		[hasContentChanged, onClose, resetContentChanged, handleOpeningCommentsPanel],
	);

	const handleViewAllClick = useCallback(
		(dialogs: DialogsStateContainer) => {
			handleOnClose(dialogs, true);
		},
		[handleOnClose],
	);

	const sidebarId = getSidebarId(isEditor, isViewCommentMode);

	const mode = isEditor ? 'edit' : 'view';

	const intl = useIntl();
	const closeCommentBox = intl.formatMessage(i18n.closeCommentBox);

	const isObjectSidebarShown = useIsObjectSidebarShown();

	const sidebarBoundingRect = sidebarEl.current?.getBoundingClientRect();
	const isSidebarExtendedBeyondViewport = (sidebarBoundingRect?.bottom ?? 0) > window.innerHeight;

	/**
	 * useEffect to handle sticky comment behavior when attached to a sticky table header.
	 * As the user scrolls, the IntersectionObserver triggers the callback the moment the table header starts/stops being sticky
	 */
	useEffect(() => {
		if (!fg('confluence_frontend_remove_duplicate_sidebar_logic')) {
			const stickyHeader = annotationElement?.closest('.pm-table-sticky-wrapper');

			if (stickyHeader && !isSidebarExtendedBeyondViewport) {
				// eslint-disable-next-line no-restricted-syntax
				const observer = new IntersectionObserver(
					([entry]) =>
						handleIntersectionOld(entry, setOldIsSticky, setSidebarOffset, sidebarOffset),
					{ threshold: 1.0 },
				);
				observer.observe(stickyHeader);

				return () => observer.disconnect();
			}
		}
	}, [annotationElement, sidebarOffset, sidebarEl, isSidebarExtendedBeyondViewport]);

	const commentSidebarIsSticky = fg('confluence_frontend_remove_duplicate_sidebar_logic')
		? isSticky
		: oldIsSticky;

	/**
	 * Below logic to set rightOffset is temporary fix.
	 * It is very fragile as there are many conditions in ObjectSidebarControl
	 * 	component to show/hide action bar and rovo button.
	 * Also values are hardcoded.
	 */
	let rightOffset = 0;
	if (mode === 'view' && isObjectSidebarShown) {
		/**
		 * Need rightOffset only when container size doesn't change when
		 * 	action bar panel (e.g details panel, comments panel).
		 * That happens currently only in view page.
		 */
		rightOffset = MIN_PANEL_WIDTH;
	} else if (
		/**
		 * When action bar or rovo button is shown, need to set rightOffset,
		 * 	otherwise long comment sidebar overlaps on it.
		 */
		!isObjectSidebarShown &&
		(fg('confluence_frontend_object_sidebar') || fg('ai-smart-button-team-25'))
	) {
		/**
		 * Width of action bar is 48px,
		 * 	got it from object-sidebar-components/src/ObjectSidebarControl.tsx.
		 * This is not ideal solution, as it is hardcoded.
		 */
		if (fg('confluence_frontend_remove_duplicate_sidebar_logic')) {
			rightOffset = 100; // add more offset from the right edge of the page so it doesn't overlap with the object sidebar
			if (mode === 'view') {
				rightOffset += commentSidebarIsSticky ? 67 : 12;
			} else {
				rightOffset += commentSidebarIsSticky ? 27 : 12;
			}
		} else {
			rightOffset = 48 + (commentSidebarIsSticky ? 52 : 12);
		}
	}

	const a11yProps = fg('confluence_frontend_comment_a11y_fixes')
		? {
				role: 'dialog',
				'aria-modal': false,
				'aria-labelledby': 'comment-sidebar-label',
			}
		: {};

	const sidebarContainerOffset = fg('confluence_frontend_remove_duplicate_sidebar_logic')
		? commentSidebarIsSticky
			? isEditor
				? sidebarOffset
					: STICKY_COMMENT_VERTICAL_OFFSET_VIEW
				: sidebarOffset
			: newSidebarOffset;

	return (
		<ErrorBoundary attribution={Attribution.COLLABORATION}>
			<Subscribe to={[DialogsStateContainer]}>
				{(dialogs: DialogsStateContainer) => (
					<ReactionsProvider contentId={pageId}>
						<UFOSegment name="comment-sidebar">
							<SidebarContainer
								offset={sidebarContainerOffset}
								rightOffset={rightOffset}
								isSticky={commentSidebarIsSticky}
								onClick={handlePropagation}
								id={sidebarId}
								data-testid={sidebarId}
								data-cy={sidebarId}
								mode={mode}
								ref={(el) => {
									sidebarEl.current = el;
									if (nudgeRef) nudgeRef.current = el;
									setRef(true);
									inlineCommentRef(el);
								}}
								{...a11yProps}
							>
								<>
									{fg('confluence_frontend_comment_a11y_fixes') && (
										<>
											<label
												id="comment-sidebar-label"
												data-testid="comment-sidebar-label"
												css={commentSidebarHiddenStyle}
											>
												{intl.formatMessage(i18n.commentSidebarLabel)}
											</label>
											<span aria-live="polite" css={commentSidebarAnnouncementContainer}>
												{showA11yAnnouncement && (
													<span data-testid="comment-sidebar-announcement">
														{intl.formatMessage(i18n.commentSidebarAnnouncement)}
													</span>
												)}
											</span>
										</>
									)}
									{!fg('confluence-frontend-redesigned-standalone-comment') && (
										<TopContainer showNavigation={Boolean(navigationOptions)}>
											{annotationId && fg('confluence-frontend-comments-panel') && (
												<Inline xcss={viewAllContainerStyles} testId="view-all-container">
													<Button
														onClick={() => handleViewAllClick(dialogs)}
														iconBefore={(iconProps) => (
															<CommentIcon
																{...iconProps}
																label="Comment Icon"
																color="var(--ds-icon-subtle)"
															/>
														)}
														appearance="subtle"
														spacing="compact"
														testId="comment-sidebar-viewall-button"
														interactionName="view-comments-menu-action"
													>
														<Text color="color.text.subtle">
															{intl.formatMessage(i18n.viewAllCommentsButtonText)}
														</Text>
													</Button>
												</Inline>
											)}
											{navigationOptions && (
												<CommentNavigation
													pageId={pageId}
													commentData={navigationOptions.commentData}
													isEditor={isEditor}
													onNavigationClick={navigationOptions.onNavigationClick}
													disableCommentCount={navigationOptions.disableCommentCount}
												/>
											)}
											<Tooltip
												content={
													/**
													 * confluence_frontend_object_header hides inline comment button
													 * confluence-frontend-comments-panel enables comments panel
													 * When either one of the feature gate is enabled, the toggle inline comments shortcut would not work
													 */
													fg('confluence_frontend_object_header') ||
													fg('confluence-frontend-comments-panel') ? (
														<FormattedMessage {...i18n.closeSidebarTooltip} />
													) : (
														<ShortcutVisualizer
															shortcut={UNIVERSAL_TOGGLE_INLINE_COMMENTS_SHORTCUT}
															isEditorShortcut={isEditor && !isLiveViewMode}
															contentBefore={<FormattedMessage {...i18n.closeSidebarTooltip} />}
														/>
													)
												}
												hideTooltipOnClick
												position="top"
											>
												<CloseButtonContainer>
													<CloseButton
														appearance="subtle"
														isUpdatedStandaloneComment={false}
														onClick={() => handleOnClose(dialogs)}
														onMouseDown={(e) => e.preventDefault()}
														testId="close-comment-sidebar-button"
														data-cy={`${isEditor ? 'editor' : 'renderer'}-close-${
															isViewCommentMode ? 'view' : 'create'
														}-comment`}
														iconBefore={
															<CrossIcon
																label={closeCommentBox}
																color={token('color.icon.subtle')}
																spacing="none"
															/>
														}
													/>
												</CloseButtonContainer>
											</Tooltip>
										</TopContainer>
									)}
									{fg('confluence-frontend-redesigned-standalone-comment') && (
										<Box xcss={[commentHeaderStyle, !isViewCommentMode && commentEditorStyle]}>
											{navigationOptions && (
												<CommentNavigation
													pageId={pageId}
													commentData={navigationOptions.commentData}
													isEditor={isEditor}
													onNavigationClick={navigationOptions.onNavigationClick}
													disableCommentCount={navigationOptions.disableCommentCount}
												/>
											)}
											{annotationId && (
												<Inline testId="redesigned-view-all-container">
													{/* eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable */}
													<Box
														xcss={redesignedViewAllStyle}
														onClick={(e: React.MouseEvent) => {
															e.stopPropagation();
															handleViewAllClick(dialogs);
														}}
													>
														<CommentIconWithUnread
															contentId={pageId}
															testId="standalone-comment-view-all-button"
															isStandaloneComment
														/>
													</Box>
												</Inline>
											)}
											<Tooltip
												content={<FormattedMessage {...i18n.closeSidebarTooltip} />}
												hideTooltipOnClick
												position="top"
											>
												<CloseButtonContainer>
													<CloseButton
														appearance="subtle"
														onClick={() => handleOnClose(dialogs)}
														onMouseDown={(e) => e.preventDefault()}
														testId="close-comment-sidebar-button"
														data-cy={`${isEditor ? 'editor' : 'renderer'}-close-${
															isViewCommentMode ? 'view' : 'create'
														}-comment`}
														iconBefore={
															<CrossIcon
																label={closeCommentBox}
																color={token('color.icon.subtle')}
																spacing="none"
															/>
														}
														isUpdatedStandaloneComment
													/>
												</CloseButtonContainer>
											</Tooltip>
										</Box>
									)}
								</>
								{children}
							</SidebarContainer>
						</UFOSegment>
					</ReactionsProvider>
				)}
			</Subscribe>
		</ErrorBoundary>
	);
};
